import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Settings, Palette, Code, Plus, Trash2 } from 'lucide-react';

// Helper functions for color conversion
const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return '';
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getAlphaFromRgba = (color) => {
  if (!color) return 1;
  if (color.startsWith('rgba')) {
    const match = color.match(/rgba\([\d\s,]+,\s*([\d.]+)\)/);
    return match ? parseFloat(match[1]) : 1;
  }
  return 1;
};

const getHexFromRgba = (color) => {
  if (!color) return '#ffffff';
  if (color.startsWith('#')) return color;
  if (color.startsWith('rgba') || color.startsWith('rgb')) {
    const match = color.match(/\d+/g);
    if (match && match.length >= 3) {
      const r = parseInt(match[0]).toString(16).padStart(2, '0');
      const g = parseInt(match[1]).toString(16).padStart(2, '0');
      const b = parseInt(match[2]).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`;
    }
  }
  return '#ffffff';
};

const BlockEditorSidebar = ({ 
  open,
  block,
  blockLibrary,
  onClose,
  updateBlockContent,
  updateBlockStyle
}) => {
  const [activeTab, setActiveTab] = useState('content');

  if (!open || !block) return null;

  const blockInfo = blockLibrary.find(b => b.type === block.type);
  const style = block.style || {};

  const handleColorWithOpacity = (colorKey, opacityKey, hexValue, opacityValue) => {
    const opacity = opacityValue !== undefined ? opacityValue : (style[opacityKey] || 1);
    const finalColor = hexToRgba(hexValue, opacity);
    updateBlockStyle(block.id, { 
      [colorKey]: finalColor,
      [opacityKey]: opacity
    });
  };

  return (
    <div className="fixed left-0 top-0 bottom-0 w-96 bg-white shadow-2xl z-50 flex flex-col border-r-2 border-gray-200">
      {/* Header */}
      <div className="p-4 border-b-2 border-gray-200 bg-gradient-to-r from-cyan-500 to-blue-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Settings size={20} />
            <h3 className="font-bold text-lg">{blockInfo?.name}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-all"
          >
            <X size={20} className="text-white" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-2 border-b border-gray-200 bg-gray-50">
        <button
          onClick={() => setActiveTab('content')}
          className={`flex-1 px-3 py-2 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
            activeTab === 'content'
              ? 'bg-cyan-500 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Settings size={14} />
          Contenu
        </button>
        <button
          onClick={() => setActiveTab('design')}
          className={`flex-1 px-3 py-2 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
            activeTab === 'design'
              ? 'bg-purple-500 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Palette size={14} />
          Design
        </button>
        <button
          onClick={() => setActiveTab('advanced')}
          className={`flex-1 px-3 py-2 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
            activeTab === 'advanced'
              ? 'bg-orange-500 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Code size={14} />
          Avancé
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'content' && (
          <ContentTab block={block} updateBlockContent={updateBlockContent} />
        )}

        {activeTab === 'design' && (
          <DesignTab 
            block={block} 
            updateBlockStyle={updateBlockStyle}
            handleColorWithOpacity={handleColorWithOpacity}
          />
        )}

        {activeTab === 'advanced' && (
          <AdvancedTab block={block} updateBlockStyle={updateBlockStyle} />
        )}
      </div>
    </div>
  );
};

// Content Tab
const ContentTab = ({ block, updateBlockContent }) => {
  return (
    <div className="space-y-4">
      {Object.keys(block.content).map((key) => {
        const value = block.content[key];
        const isLongText = typeof value === 'string' && value.length > 50;
        const isHTML = typeof value === 'string' && value.includes('<');
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

        return (
          <div key={key} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <Label className="text-xs font-bold text-gray-700 mb-2 block uppercase">{label}</Label>

            {Array.isArray(value) ? (
              <div className="space-y-2">
                {value.map((item, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={typeof item === 'string' ? item : item.content || ''}
                      onChange={(e) => {
                        const newArray = [...value];
                        if (typeof item === 'string') {
                          newArray[idx] = e.target.value;
                        } else {
                          newArray[idx] = { ...item, content: e.target.value };
                        }
                        updateBlockContent(block.id, { [key]: newArray });
                      }}
                      className="flex-1 text-sm"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const newArray = value.filter((_, i) => i !== idx);
                        updateBlockContent(block.id, { [key]: newArray });
                      }}
                      className="border-red-400 text-red-600 px-2"
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const newItem = typeof value[0] === 'string' ? '' : { content: '' };
                    updateBlockContent(block.id, { [key]: [...value, newItem] });
                  }}
                  className="w-full border-cyan-400 text-cyan-600 text-sm"
                >
                  <Plus size={12} className="mr-1" />
                  Ajouter
                </Button>
              </div>
            ) : isHTML || isLongText ? (
              <textarea
                value={value}
                onChange={(e) => updateBlockContent(block.id, { [key]: e.target.value })}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg font-mono text-xs"
                rows={isHTML ? "6" : "3"}
              />
            ) : key === 'level' ? (
              <select
                value={value}
                onChange={(e) => updateBlockContent(block.id, { [key]: e.target.value })}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="h1">Titre 1 (H1)</option>
                <option value="h2">Titre 2 (H2)</option>
                <option value="h3">Titre 3 (H3)</option>
              </select>
            ) : key === 'textAlign' ? (
              <select
                value={value}
                onChange={(e) => updateBlockContent(block.id, { [key]: e.target.value })}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="left">Gauche</option>
                <option value="center">Centre</option>
                <option value="right">Droite</option>
              </select>
            ) : key === 'fontSize' ? (
              <select
                value={value}
                onChange={(e) => updateBlockContent(block.id, { [key]: e.target.value })}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="sm">Petit</option>
                <option value="base">Normal</option>
                <option value="lg">Grand</option>
                <option value="xl">Très grand</option>
              </select>
            ) : typeof value === 'number' ? (
              <Input
                type="number"
                value={value}
                onChange={(e) => updateBlockContent(block.id, { [key]: parseInt(e.target.value) || 0 })}
                className="text-sm"
              />
            ) : (
              <Input
                value={value}
                onChange={(e) => updateBlockContent(block.id, { [key]: e.target.value })}
                className="text-sm"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

// Design Tab avec marges et padding améliorés
const DesignTab = ({ block, updateBlockStyle, handleColorWithOpacity }) => {
  const style = block.style || {};

  return (
    <div className="space-y-4">
      {/* Background */}
      <div className="bg-white p-3 rounded-lg border border-gray-200">
        <h4 className="font-bold text-sm mb-3 text-gray-900">🎨 Arrière-plan</h4>
        
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Couleur</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="color"
                value={getHexFromRgba(style.backgroundColor)}
                onChange={(e) => handleColorWithOpacity('backgroundColor', 'backgroundOpacity', e.target.value, style.backgroundOpacity)}
                className="w-12 h-8"
              />
              <Input
                type="text"
                value={style.backgroundColor || ''}
                onChange={(e) => updateBlockStyle(block.id, { backgroundColor: e.target.value })}
                placeholder="rgba(0,0,0,0.5)"
                className="flex-1 text-xs"
              />
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={style.backgroundOpacity || getAlphaFromRgba(style.backgroundColor)}
              onChange={(e) => {
                const opacity = parseFloat(e.target.value);
                const hex = getHexFromRgba(style.backgroundColor);
                handleColorWithOpacity('backgroundColor', 'backgroundOpacity', hex, opacity);
              }}
              className="w-full mt-1 h-1"
            />
            <p className="text-xs text-gray-500 mt-1">{Math.round((style.backgroundOpacity || getAlphaFromRgba(style.backgroundColor)) * 100)}%</p>
          </div>

          <div>
            <Label className="text-xs">Image (URL)</Label>
            <Input
              type="text"
              value={style.backgroundImage || ''}
              onChange={(e) => updateBlockStyle(block.id, { backgroundImage: e.target.value })}
              placeholder="https://..."
              className="text-xs mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">Vidéo YouTube</Label>
            <Input
              type="text"
              value={style.backgroundVideo || ''}
              onChange={(e) => updateBlockStyle(block.id, { backgroundVideo: e.target.value })}
              placeholder="https://youtube.com/watch?v=..."
              className="text-xs mt-1"
            />
          </div>
        </div>
      </div>

      {/* Spacing - AMÉLIORE */}
      <div className="bg-white p-3 rounded-lg border border-gray-200">
        <h4 className="font-bold text-sm mb-3 text-gray-900">📏 Espacement</h4>
        
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold mb-2 block">Padding (intérieur)</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-gray-600">Haut</Label>
                <Input
                  type="number"
                  value={style.padding?.top || '16'}
                  onChange={(e) => updateBlockStyle(block.id, { 
                    padding: { ...(style.padding || {}), top: e.target.value }
                  })}
                  className="text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Droite</Label>
                <Input
                  type="number"
                  value={style.padding?.right || '16'}
                  onChange={(e) => updateBlockStyle(block.id, { 
                    padding: { ...(style.padding || {}), right: e.target.value }
                  })}
                  className="text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Bas</Label>
                <Input
                  type="number"
                  value={style.padding?.bottom || '16'}
                  onChange={(e) => updateBlockStyle(block.id, { 
                    padding: { ...(style.padding || {}), bottom: e.target.value }
                  })}
                  className="text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Gauche</Label>
                <Input
                  type="number"
                  value={style.padding?.left || '16'}
                  onChange={(e) => updateBlockStyle(block.id, { 
                    padding: { ...(style.padding || {}), left: e.target.value }
                  })}
                  className="text-xs mt-1"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold mb-2 block">Margin (extérieur)</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-gray-600">Haut</Label>
                <Input
                  type="number"
                  value={style.margin?.top || '0'}
                  onChange={(e) => updateBlockStyle(block.id, { 
                    margin: { ...(style.margin || {}), top: e.target.value }
                  })}
                  className="text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Droite</Label>
                <Input
                  type="number"
                  value={style.margin?.right || '0'}
                  onChange={(e) => updateBlockStyle(block.id, { 
                    margin: { ...(style.margin || {}), right: e.target.value }
                  })}
                  className="text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Bas</Label>
                <Input
                  type="number"
                  value={style.margin?.bottom || '0'}
                  onChange={(e) => updateBlockStyle(block.id, { 
                    margin: { ...(style.margin || {}), bottom: e.target.value }
                  })}
                  className="text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Gauche</Label>
                <Input
                  type="number"
                  value={style.margin?.left || '0'}
                  onChange={(e) => updateBlockStyle(block.id, { 
                    margin: { ...(style.margin || {}), left: e.target.value }
                  })}
                  className="text-xs mt-1"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Typography */}
      <div className="bg-white p-3 rounded-lg border border-gray-200">
        <h4 className="font-bold text-sm mb-3 text-gray-900">🔤 Typographie</h4>
        
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Police</Label>
            <select
              value={style.fontFamily || ''}
              onChange={(e) => updateBlockStyle(block.id, { fontFamily: e.target.value })}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs mt-1"
            >
              <option value="">Par défaut</option>
              <option value="'Poppins', sans-serif">Poppins</option>
              <option value="'Roboto', sans-serif">Roboto</option>
              <option value="'Inter', sans-serif">Inter</option>
              <option value="'Arial', sans-serif">Arial</option>
              <option value="'Times New Roman', serif">Times New Roman</option>
              <option value="'Georgia', serif">Georgia</option>
              <option value="'Courier New', monospace">Courier New</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Taille (px)</Label>
              <Input
                type="number"
                value={style.fontSize || ''}
                onChange={(e) => updateBlockStyle(block.id, { fontSize: e.target.value })}
                placeholder="16"
                className="text-xs mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Poids</Label>
              <select
                value={style.fontWeight || ''}
                onChange={(e) => updateBlockStyle(block.id, { fontWeight: e.target.value })}
                className="w-full px-2 py-1 border border-gray-300 rounded-lg text-xs mt-1"
              >
                <option value="">Normal</option>
                <option value="300">Light (300)</option>
                <option value="400">Regular (400)</option>
                <option value="500">Medium (500)</option>
                <option value="600">Semi-Bold (600)</option>
                <option value="700">Bold (700)</option>
                <option value="800">Extra-Bold (800)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Hauteur ligne</Label>
              <Input
                type="number"
                step="0.1"
                value={style.lineHeight || ''}
                onChange={(e) => updateBlockStyle(block.id, { lineHeight: e.target.value })}
                placeholder="1.5"
                className="text-xs mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Espacement lettres (px)</Label>
              <Input
                type="number"
                step="0.1"
                value={style.letterSpacing || ''}
                onChange={(e) => updateBlockStyle(block.id, { letterSpacing: e.target.value })}
                placeholder="0"
                className="text-xs mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Style</Label>
            <select
              value={style.fontStyle || ''}
              onChange={(e) => updateBlockStyle(block.id, { fontStyle: e.target.value })}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs mt-1"
            >
              <option value="">Normal</option>
              <option value="italic">Italic</option>
              <option value="oblique">Oblique</option>
            </select>
          </div>

          <div>
            <Label className="text-xs">Transformation</Label>
            <select
              value={style.textTransform || ''}
              onChange={(e) => updateBlockStyle(block.id, { textTransform: e.target.value })}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs mt-1"
            >
              <option value="">Aucune</option>
              <option value="uppercase">MAJUSCULES</option>
              <option value="lowercase">minuscules</option>
              <option value="capitalize">Première Lettre</option>
            </select>
          </div>

          <div>
            <Label className="text-xs">Décoration</Label>
            <select
              value={style.textDecoration || ''}
              onChange={(e) => updateBlockStyle(block.id, { textDecoration: e.target.value })}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-xs mt-1"
            >
              <option value="">Aucune</option>
              <option value="underline">Souligné</option>
              <option value="overline">Ligne au-dessus</option>
              <option value="line-through">Barré</option>
            </select>
          </div>

          <div>
            <Label className="text-xs">Couleur du texte</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="color"
                value={getHexFromRgba(style.textColor)}
                onChange={(e) => handleColorWithOpacity('textColor', 'textOpacity', e.target.value, style.textOpacity)}
                className="w-12 h-8"
              />
              <Input
                type="text"
                value={style.textColor || ''}
                onChange={(e) => updateBlockStyle(block.id, { textColor: e.target.value })}
                placeholder="rgba(0,0,0,1)"
                className="flex-1 text-xs"
              />
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={style.textOpacity || getAlphaFromRgba(style.textColor)}
              onChange={(e) => {
                const opacity = parseFloat(e.target.value);
                const hex = getHexFromRgba(style.textColor);
                handleColorWithOpacity('textColor', 'textOpacity', hex, opacity);
              }}
              className="w-full mt-1 h-1"
            />
            <p className="text-xs text-gray-500 mt-1">{Math.round((style.textOpacity || getAlphaFromRgba(style.textColor)) * 100)}%</p>
          </div>
        </div>
      </div>

      {/* Border */}
      <div className="bg-white p-3 rounded-lg border border-gray-200">
        <h4 className="font-bold text-sm mb-3 text-gray-900">🔲 Bordure</h4>
        
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Arrondi (px)</Label>
              <Input
                type="number"
                value={style.borderRadius || '0'}
                onChange={(e) => updateBlockStyle(block.id, { borderRadius: e.target.value })}
                className="text-xs mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Épaisseur (px)</Label>
              <Input
                type="number"
                value={style.borderWidth || '0'}
                onChange={(e) => updateBlockStyle(block.id, { borderWidth: e.target.value })}
                className="text-xs mt-1"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-xs">Couleur</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="color"
                value={style.borderColor || '#000000'}
                onChange={(e) => updateBlockStyle(block.id, { borderColor: e.target.value })}
                className="w-12 h-8"
              />
              <Input
                type="text"
                value={style.borderColor || ''}
                onChange={(e) => updateBlockStyle(block.id, { borderColor: e.target.value })}
                placeholder="#000000"
                className="flex-1 text-xs"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Advanced Tab
const AdvancedTab = ({ block, updateBlockStyle }) => {
  const style = block.style || {};

  return (
    <div className="space-y-4">
      <div className="bg-white p-3 rounded-lg border border-gray-200">
        <h4 className="font-bold text-sm mb-3 text-gray-900">📐 Dimensions</h4>
        
        <div className="space-y-2">
          <div>
            <Label className="text-xs">Largeur (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={style.width || '100'}
              onChange={(e) => updateBlockStyle(block.id, { width: e.target.value })}
              className="text-xs mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Hauteur min (px)</Label>
            <Input
              type="number"
              value={style.minHeight || ''}
              onChange={(e) => updateBlockStyle(block.id, { minHeight: e.target.value })}
              className="text-xs mt-1"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockEditorSidebar;
